import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Field — the wrapper that makes a labelled control correct by construction.
 *
 * Four ids have to agree for a form control to be usable by a reader: the label's `htmlFor` and the
 * control's `id`, and the description's and error's ids inside the control's `aria-describedby`. Every
 * one of those is a place a hand-assembled form drifts — the dashboard being replaced has inputs whose
 * error text sits in a sibling `<p>` associated with nothing, so a reader focusing the field is told
 * the label and never told why it was rejected.
 *
 * So the control does not receive its own wiring attributes from the caller. `Field` generates the
 * ids, and the caller renders the control with the props it is handed:
 *
 * ```tsx
 * <Field label="Email" error={problem}>
 *   {(control) => <Input type="email" autoComplete="username" {...control} />}
 * </Field>
 * ```
 *
 * A render prop rather than cloning a child: cloning would mean guessing which element in an arbitrary
 * subtree is the control, and getting that wrong silently produces exactly the unassociated markup
 * this is here to prevent.
 *
 * The error is `role="alert"`, so it is announced when it appears rather than only when the field is
 * next focused, and it carries an `aria-hidden` glyph so the state is not signalled by colour alone.
 */
import { useId } from 'react';
import { cn } from '../lib/cn.js';
import { Label } from './label.js';
export const Field = ({ label, controlId: suppliedControlId, description, error, required = false, className, children, }) => {
    const base = useId();
    const controlId = suppliedControlId ?? `${base}-control`;
    const descriptionId = `${controlId}-description`;
    const errorId = `${controlId}-error`;
    const describedBy = [
        description === undefined ? undefined : descriptionId,
        error === undefined ? undefined : errorId,
    ]
        .filter((id) => id !== undefined)
        .join(' ');
    return (_jsxs("div", { className: cn('flex flex-col gap-2 font-body', className), children: [_jsx(Label, { htmlFor: controlId, requiredMark: required, children: label }), children({
                id: controlId,
                'aria-describedby': describedBy === '' ? undefined : describedBy,
                'aria-invalid': error === undefined ? undefined : true,
                'aria-required': required ? true : undefined,
                required: required ? true : undefined,
            }), description === undefined ? null : (_jsx("p", { id: descriptionId, className: "m-0 text-sm text-muted-foreground", children: description })), error === undefined ? null : (_jsxs("p", { id: errorId, role: "alert", className: "m-0 flex items-center gap-2 text-sm text-foreground", children: [_jsx("svg", { className: "h-4 w-4 shrink-0", viewBox: "0 0 24 24", fill: "none", "aria-hidden": "true", focusable: "false", children: _jsx("path", { d: "M12 4v11m0 4.5v.5", stroke: "currentColor", strokeWidth: "2.5", strokeLinecap: "round" }) }), error] }))] }));
};
//# sourceMappingURL=field.js.map